import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Play,
  Pause,
  SkipForward,
  StopCircle,
  Users,
  Trophy,
  Clock,
  CheckCircle2,
  XCircle,
  Eye,
  BarChart3,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import api from '@/lib/api';

const BACKEND_URL = window.location.origin;

export default function QuizLiveHost() {
  const { sessionCode } = useParams();
  const navigate = useNavigate();
  const wsRef = useRef(null);
  
  const [session, setSession] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [rankings, setRankings] = useState([]);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [totalPlayers, setTotalPlayers] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [timeLeft, setTimeLeft] = useState(30);
  const [loading, setLoading] = useState(true);
  const [gameFinished, setGameFinished] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    loadSession();
    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [sessionCode]);

  const loadSession = async () => {
    try {
      const [sessionRes, quizRes] = await Promise.all([
        api.get(`/quiz-live/sessions/${sessionCode}`),
        api.get(`/quiz-live/sessions/${sessionCode}`).then(r => 
          api.get(`/quiz-live/quizzes/${r.data.quiz_id}`)
        )
      ]);
      
      setSession(sessionRes.data);
      setQuiz(quizRes.data);
      setRankings(sessionRes.data.players || []);
      setTotalPlayers(sessionRes.data.players?.length || 0);
      
      if (sessionRes.data.current_question_index >= 0) {
        setQuestionIndex(sessionRes.data.current_question_index);
        setCurrentQuestion(quizRes.data.questions[sessionRes.data.current_question_index]);
        startTimer(quizRes.data.questions[sessionRes.data.current_question_index].time_limit || 30);
      }
    } catch (error) {
      console.error('Error loading session:', error);
      toast.error('Erro ao carregar sessão');
      navigate('/quiz-live');
    } finally {
      setLoading(false);
    }
  };

  const connectWebSocket = () => {
    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/quiz-live/ws/host/${sessionCode}`);
    
    ws.onopen = () => {
      console.log('Host WebSocket connected');
    };
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log('Host WS message:', data);
      
      if (data.type === 'player_answered') {
        setAnsweredCount(data.answered_count);
        setTotalPlayers(data.total_players);
        setRankings(data.rankings || []);
      }
      
      if (data.type === 'game_finished') {
        setRankings(data.rankings);
        setGameFinished(true);
        if (timerRef.current) clearInterval(timerRef.current);
      }
    };
    
    ws.onclose = () => {
      console.log('Host WebSocket disconnected');
    };
    
    wsRef.current = ws;
    
    // Keep-alive
    const pingInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);
    
    return () => clearInterval(pingInterval);
  };

  const startTimer = (duration) => {
    setTimeLeft(duration);
    if (timerRef.current) clearInterval(timerRef.current);
    
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleShowAnswer = () => {
    setShowAnswer(true);
    if (timerRef.current) clearInterval(timerRef.current);
    
    // Broadcast to players
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'show_answer' }));
    }
  };

  const handleNextQuestion = async () => {
    try {
      const response = await api.post(`/quiz-live/sessions/${sessionCode}/next`);
      
      if (response.data.status === 'finished') {
        setGameFinished(true);
        return;
      }
      
      const nextIndex = response.data.question_index;
      setQuestionIndex(nextIndex);
      setCurrentQuestion(quiz.questions[nextIndex]);
      setShowAnswer(false);
      setAnsweredCount(0);
      startTimer(quiz.questions[nextIndex].time_limit || 30);
    } catch (error) {
      console.error('Error moving to next question:', error);
      toast.error('Erro ao avançar pergunta');
    }
  };

  const handleEndGame = async () => {
    if (!confirm('Tem certeza que deseja encerrar o jogo?')) return;
    
    try {
      await api.post(`/quiz-live/sessions/${sessionCode}/end`);
      setGameFinished(true);
    } catch (error) {
      console.error('Error ending game:', error);
      toast.error('Erro ao encerrar jogo');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  // Final Results Screen
  if (gameFinished) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <Trophy className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-white mb-2">Fim do Jogo!</h1>
            <p className="text-gray-400">{quiz?.title}</p>
          </div>

          {/* Podium */}
          <div className="flex items-end justify-center gap-4 mb-8">
            {/* 2nd Place */}
            {rankings[1] && (
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-gray-400 to-gray-500 flex items-center justify-center mx-auto mb-2 text-white text-2xl font-bold">
                  {rankings[1].name.charAt(0)}
                </div>
                <div className="bg-gray-500 rounded-t-xl p-4 h-28 w-28 flex flex-col items-center justify-end">
                  <span className="text-3xl font-black text-white">2º</span>
                  <span className="text-white font-bold truncate w-full">
                    {rankings[1].name}
                  </span>
                  <span className="text-white/80 text-sm">{rankings[1].score}pts</span>
                </div>
              </div>
            )}
            
            {/* 1st Place */}
            {rankings[0] && (
              <div className="text-center">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center mx-auto mb-2 text-white text-3xl font-bold ring-4 ring-yellow-300 animate-pulse">
                  {rankings[0].name.charAt(0)}
                </div>
                <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-t-xl p-4 h-36 w-32 flex flex-col items-center justify-end">
                  <Trophy className="w-8 h-8 text-white mb-1" />
                  <span className="text-4xl font-black text-white">1º</span>
                  <span className="text-white font-bold truncate w-full">
                    {rankings[0].name}
                  </span>
                  <span className="text-white/80">{rankings[0].score}pts</span>
                </div>
              </div>
            )}
            
            {/* 3rd Place */}
            {rankings[2] && (
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-600 to-orange-700 flex items-center justify-center mx-auto mb-2 text-white text-2xl font-bold">
                  {rankings[2].name.charAt(0)}
                </div>
                <div className="bg-orange-700 rounded-t-xl p-4 h-24 w-28 flex flex-col items-center justify-end">
                  <span className="text-3xl font-black text-white">3º</span>
                  <span className="text-white font-bold truncate w-full">
                    {rankings[2].name}
                  </span>
                  <span className="text-white/80 text-sm">{rankings[2].score}pts</span>
                </div>
              </div>
            )}
          </div>

          {/* Full Rankings */}
          <Card className="bg-white/5 border-white/10 mb-6">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Ranking Final
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {rankings.map((player, index) => (
                  <div
                    key={player.id}
                    className={`flex items-center gap-4 p-4 rounded-xl ${
                      index < 3 ? 'bg-white/10' : 'bg-white/5'
                    }`}
                  >
                    <span className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                      index === 0 ? 'bg-yellow-500 text-white' :
                      index === 1 ? 'bg-gray-400 text-white' :
                      index === 2 ? 'bg-orange-600 text-white' :
                      'bg-white/20 text-white'
                    }`}>
                      {index + 1}
                    </span>
                    <div className="flex-1">
                      <p className="text-white font-medium">{player.name}</p>
                      <p className="text-sm text-gray-400">
                        {player.correct_answers} de {player.total_answers} acertos
                      </p>
                    </div>
                    <span className="text-xl font-bold text-purple-400">
                      {player.score}pts
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-4 justify-center">
            <Button
              variant="outline"
              onClick={() => navigate('/quiz-live')}
              className="border-white/20 text-white"
            >
              Voltar ao Início
            </Button>
            <Button
              onClick={() => navigate(`/quiz-live/lobby/${sessionCode.slice(0, -1)}${Math.random().toString(36).charAt(2).toUpperCase()}`)}
              className="bg-gradient-to-r from-purple-600 to-green-500"
            >
              Jogar Novamente
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const correctOption = currentQuestion?.options?.find(o => o.is_correct);
  const progress = totalPlayers > 0 ? (answeredCount / totalPlayers) * 100 : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950">
      {/* Header */}
      <div className="bg-slate-900/80 border-b border-white/10 p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-white">{quiz?.title}</h1>
            <p className="text-sm text-gray-400">
              Questão {questionIndex + 1} de {quiz?.questions?.length || 0}
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            <Badge variant="outline" className="border-white/30 text-white">
              <Users className="w-4 h-4 mr-1" />
              {totalPlayers} jogadores
            </Badge>
            
            <Button
              variant="destructive"
              size="sm"
              onClick={handleEndGame}
            >
              <StopCircle className="w-4 h-4 mr-2" />
              Encerrar
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 lg:p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Question */}
          <div className="lg:col-span-2 space-y-4">
            {/* Timer and Progress */}
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-gray-400">
                    <Users className="w-4 h-4" />
                    <span>{answeredCount} / {totalPlayers} responderam</span>
                  </div>
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                    timeLeft <= 5 ? 'bg-red-500 animate-pulse' : 'bg-white/10'
                  }`}>
                    <Clock className="w-4 h-4 text-white" />
                    <span className="text-white font-bold text-lg">{timeLeft}s</span>
                  </div>
                </div>
                <Progress value={progress} className="h-3" />
              </CardContent>
            </Card>

            {/* Question */}
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-6">
                <Badge className="mb-4 bg-purple-600">
                  Questão {questionIndex + 1}
                </Badge>
                
                <h2 className="text-2xl font-bold text-white mb-8">
                  {currentQuestion?.question}
                </h2>

                {/* Options */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {currentQuestion?.options?.map((option, index) => {
                    const isCorrect = option.is_correct;
                    const colors = [
                      'border-red-500 bg-red-500/20',
                      'border-blue-500 bg-blue-500/20',
                      'border-yellow-500 bg-yellow-500/20',
                      'border-green-500 bg-green-500/20'
                    ];
                    
                    return (
                      <div
                        key={option.id}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          showAnswer && isCorrect
                            ? 'border-green-500 bg-green-500/30 scale-105'
                            : showAnswer
                            ? 'opacity-50'
                            : colors[index % 4]
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white font-bold">
                            {String.fromCharCode(65 + index)}
                          </span>
                          <span className="flex-1 text-white font-medium">
                            {option.text}
                          </span>
                          {showAnswer && isCorrect && (
                            <CheckCircle2 className="w-6 h-6 text-green-400" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Explanation */}
                {showAnswer && currentQuestion?.explanation && (
                  <div className="mt-6 p-4 bg-blue-500/20 border border-blue-500/50 rounded-xl">
                    <p className="text-blue-200 font-medium mb-1">Explicação:</p>
                    <p className="text-white">{currentQuestion.explanation}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Controls */}
            <div className="flex gap-4">
              {!showAnswer ? (
                <Button
                  onClick={handleShowAnswer}
                  className="flex-1 h-14 bg-amber-500 hover:bg-amber-600"
                  data-testid="show-answer-btn"
                >
                  <Eye className="w-5 h-5 mr-2" />
                  Mostrar Resposta
                </Button>
              ) : (
                <Button
                  onClick={handleNextQuestion}
                  className="flex-1 h-14 bg-gradient-to-r from-purple-600 to-green-500"
                  data-testid="next-question-btn"
                >
                  <SkipForward className="w-5 h-5 mr-2" />
                  {questionIndex + 1 >= (quiz?.questions?.length || 0)
                    ? 'Ver Resultados'
                    : 'Próxima Pergunta'
                  }
                </Button>
              )}
            </div>
          </div>

          {/* Sidebar - Rankings */}
          <div className="lg:col-span-1">
            <Card className="bg-white/5 border-white/10 sticky top-24">
              <CardHeader className="pb-3">
                <CardTitle className="text-white flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-yellow-400" />
                  Ranking ao Vivo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {rankings.length === 0 ? (
                    <p className="text-gray-400 text-center py-8">
                      Aguardando respostas...
                    </p>
                  ) : (
                    rankings.slice(0, 15).map((player, index) => (
                      <div
                        key={player.id}
                        className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                          index < 3 ? 'bg-white/10' : 'bg-white/5'
                        }`}
                      >
                        <span className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-sm ${
                          index === 0 ? 'bg-yellow-500 text-white' :
                          index === 1 ? 'bg-gray-400 text-white' :
                          index === 2 ? 'bg-orange-600 text-white' :
                          'bg-white/20 text-white'
                        }`}>
                          {index + 1}
                        </span>
                        <span className="flex-1 text-white text-sm truncate">
                          {player.name}
                        </span>
                        <span className="text-purple-400 font-bold text-sm">
                          {player.score}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
